/* eslint-disable react/jsx-handler-names */
import { FormEvent, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import Button from 'components/Button'
import { IconEyeHide } from 'components/Icon/IconEyeHide'
import { IconEyeShow } from 'components/Icon/IconEyeShow'
import { getErrorMessageController } from 'utils/react-hook-form'

/**
 * Defines the props for the MyTextField component
 */
type TextFieldProps = {
  name: string
  /**
   * Determines if the field as aria-required. If set to false, and rules.required is true, this value will be overridden and shown as true.
   */
  'aria-required'?: boolean
  ariaLabel?: string
  label?: React.ReactNode
  hint?: React.ReactNode
  type?: string
  inputMode?:
    | 'text'
    | 'numeric'
    | 'none'
    | 'tel'
    | 'url'
    | 'email'
    | 'decimal'
    | 'search'
  multiline?: boolean
  className?: string
  fieldClassName?: string
  labelClassName?: string
  errorMessageClassName?: string
  autoComplete?: string
  placeholder?: string
  isCurrentlyValid?: boolean
  maxLength?: number
  addErrorPrefix?: boolean
  onBlur?: () => void
  /**
   * Defines a pattern to replace when input is given to the textfield.
   * This is usually similar, but different than pattern validation
   * rules. For instance, this regex might be /^a/g meaning to replace
   * all characters that are not 'a', while the matching validation
   * rule would be /^[a]+$/, meaning that it must start and only
   * contain a's
   */
  pattern?: RegExp | string
  /**
   * Defines whether to display a show/hide "eye" icon
   * to toggle between password and text types
   */
  visibilityToggle?: boolean
} & Omit<Parameters<typeof Controller>[0], 'render' | 'control'>

type TextFieldFormEvent = FormEvent<HTMLInputElement> &
  FormEvent<HTMLTextAreaElement>

export const MyTextField = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  className,
  hint,
  label,
  labelClassName,
  inputMode,
  fieldClassName,
  pattern,
  errorMessageClassName,
  multiline,
  ariaLabel,
  isCurrentlyValid,
  addErrorPrefix, // TODO - set this automatically to AddErrorPrefix = true, once MCF has this added in
  onBlur,
  visibilityToggle,
  ...props
}: TextFieldProps) => {
  const {
    'aria-required': ariaRequired,
    ...otherProps
  } = props

  const { control } = useFormContext()

  const { t } = useTranslation('common')
  const [isVisible, setIsVisible] = useState(false)

  const type = useMemo(() => {
    return visibilityToggle
      ? isVisible
        ? 'text'
        : 'password'
      : props.type ?? 'text'
  }, [props.type, visibilityToggle, isVisible])

  const toggleType = () => {
    setIsVisible((prevState) => !prevState)
  }

  return (
    <Controller
      rules={rules}
      control={control}
      name={name}
      shouldUnregister={shouldUnregister}
      defaultValue={defaultValue}
      render={({ field, fieldState }) => {
        const { ref } = field
        const ComponentType = multiline ? 'textarea' : 'input'
        return (
          // Don't use TextField here because we do not want role=alert in our error messages
          <div className={className}>
            <label
              className={`ds-c-label ${labelClassName ?? ''}`}
              htmlFor={name}
            >
              <span>{label}</span>
              {hint ? <div className="ds-c-field__hint">{hint}</div> : null}
            </label>
            <div
              className={classNames(
                'field-wrapper ds-c-field',
                fieldClassName,
                {
                  'ds-c-field--error':
                    fieldState.error || isCurrentlyValid === false
                }
              )}
            >
              <ComponentType
                // {...field}
                aria-describedby={
                  fieldState.error ? `error-${field.name}` : undefined
                }
                name={field.name}
                ref={ref}
                onChange={field.onChange}
                onInput={(event: TextFieldFormEvent) => {
                  if (pattern) {
                    // Replace characters and maintain the cursor position in the field
                    const cursorStart = event.currentTarget.selectionStart
                    let charactersRemoved = 0
                    event.currentTarget.value =
                      event.currentTarget.value.replace(pattern, () => {
                        charactersRemoved++
                        return ''
                      })
                    const newCursorStart = cursorStart - charactersRemoved
                    event.currentTarget.selectionStart = newCursorStart
                    event.currentTarget.selectionEnd = newCursorStart
                  }
                }}
                onBlur={() => {
                  onBlur?.()
                  field.onBlur()
                }}
                value={field.value}
                id={name}
                className="field-wrapper__input"
                // Using a tertiary operator so it will be undefined if aria-required isn't set instead of always true or false
                aria-required={rules?.required ? true : ariaRequired}
                aria-invalid={!!fieldState.error}
                aria-label={ariaLabel}
                {...otherProps}
                type={type}
              />
              {visibilityToggle && (
                <Button
                  className={classNames(
                    'my-u-p--2 ds-c-button ds-c-button--ghost ds-u-text-decoration--none'
                  )}
                  onClick={toggleType}
                >
                  <div className="ds-u-display--flex ds-u-align-items--center">
                    <div className="my-u-mr--1">
                      {type === 'password' ? <IconEyeShow /> : <IconEyeHide />}
                    </div>
                    <div className="my-u-pt--1 ">
                      {type === 'password' ? t('show') : t('hide')}
                    </div>
                  </div>
                </Button>
              )}
            </div>
            {(fieldState.error || isCurrentlyValid === false) && (
              <span
                id={`error-${field.name}`}
                className={`error-message ds-c-field__error-message ${
                  errorMessageClassName ?? ''
                }`}
              >
                {getErrorMessageController(fieldState, addErrorPrefix)}
              </span>
            )}
          </div>
        )
      }}
    />
  )
}
